import { ReactNode, useState } from "react";
import { Button, Input, Segmented, Table, Tooltip, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { CalendarOutlined, ReloadOutlined } from "@ant-design/icons";
import "../../css/dashboard-table.css";

export type PeriodT = "hoy" | "ayer" | "semana" | "mes";

export const PERIOD_OPTIONS: { value: PeriodT; label: string }[] = [
  { value: "hoy", label: "Hoy" },
  { value: "ayer", label: "Ayer" },
  { value: "semana", label: "Esta semana" },
  { value: "mes", label: "Este mes" },
];

export const PAGE_SIZE = 10;

interface DashboardTableProps<T> {
  columns: ColumnsType<T>;
  data: T[];
  total: number;
  loading: boolean;
  pageIndex: number;
  pageSize?: number;
  period: PeriodT;
  onPeriodChange: (period: PeriodT) => void;
  onPageChange: (pageIndex: number) => void;
  onSearch: (value: string) => void;
  onRefresh: () => void;
  extra?: ReactNode;
}

export const DashboardTable = <T extends { id?: number | string }>({
  columns,
  data,
  total,
  loading,
  pageIndex,
  pageSize = PAGE_SIZE,
  period,
  onPeriodChange,
  onPageChange,
  onSearch,
  onRefresh,
  extra,
}: DashboardTableProps<T>) => (
  <div className="dash-table">
    <div className="dash-table-toolbar">
      <Input.Search
        allowClear
        placeholder="Buscar"
        onSearch={(value) => {
          onPageChange(0);
          onSearch(value);
        }}
        style={{ flex: "1 1 180px", maxWidth: 300 }}
      />
      {extra}
      <Tooltip title="Refrescar">
        <Button icon={<ReloadOutlined />} onClick={onRefresh} />
      </Tooltip>
      <div className="dash-period">
        <span className="dash-period-label">
          <CalendarOutlined /> Periodo
        </span>
        <Segmented
          value={period}
          options={PERIOD_OPTIONS}
          onChange={(value) => {
            onPageChange(0);
            onPeriodChange(value as PeriodT);
          }}
        />
      </div>
    </div>
    <Table<T>
      size="small"
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      scroll={{ x: "max-content", y: "max(280px, calc(100vh - 640px))" }}
      locale={{ emptyText: "Sin registros para este periodo" }}
      pagination={{
        current: pageIndex + 1,
        pageSize,
        total,
        showSizeChanger: false,
        simple: window.innerWidth < 576,
        onChange: (page) => onPageChange(page - 1),
      }}
    />
  </div>
);

/** Descarga un PDF en base64 devuelto por el API, con mensajes de estado. */
export const usePdfDownload = (prefix: string) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const download = async (
    generator: (id: number) => Promise<{ data?: any }>,
    id: number,
    container: string
  ) => {
    try {
      setLoadingId(id);
      messageApi.open({
        key: "dashboardPdf",
        type: "loading",
        content: "Generando Pdf",
        duration: 0,
      });
      const { data } = await generator(id);
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${data}`;
      link.download = `${prefix}-${container}.pdf`;
      link.click();
      messageApi.open({
        key: "dashboardPdf",
        type: "success",
        content: "PDF Descargado",
        duration: 2,
      });
    } catch (err: any) {
      messageApi.open({
        key: "dashboardPdf",
        type: "error",
        content: `Error al generar el pdf: ${err.toString()}`,
        duration: 4,
      });
    } finally {
      setLoadingId(null);
    }
  };

  return { download, loadingId, contextHolder };
};
